import React from "react";
import PropTypes from "prop-types";
import Radium from "radium";
import {
  asNumberString,
  asPercentageString,
  asPercentageStringDecimal
} from "../../utils/formatters";
import AnimatedNumber from "react-animated-number";
import { Tooltip } from "react-tooltip";
import { handleTooltip } from "../../utils/handleToolTip";
import { currencySymbols, POSITION } from "../../utils/currency-position";
import PercentChanged from "../percentChanged/percentChanged";
import numbro from "numbro";
import "./dataDisplay.scss";

const DEFAULT_COLOR = "#4a9bff";

function DataDisplay(props) {
  props = {
    url: "",
    toolTipContent: "",
    value: 23,
    size: 46,
    color: DEFAULT_COLOR,
    duration: props.disableAnimation ? 0 : 1000,
    isPercentage: false,
    showDecimal: false,
    toFixedValue: null,
    sectionProps: {},
    customValue: props.customValue,
    toolTipId: "data-point-tooltip",
    ...props
  };

  const colorStyle = props.color !== DEFAULT_COLOR ? { color: props.color } : {};

  const style = {
    margin: 0,
    padding: 0,
    display: "inline",
    fontWeight: "100",
    transition: "0.8s ease-out",
    fontSize: props.size,
    ...colorStyle,
    ...props.customStyles
  };

  const formatValueNumber = (n) => {
    return asNumberString(n, props.value) + `${props.isPercentage ? "%" : ""}`;
  };

  const formatValuePercent = (n) =>
    props.showDecimal
      ? asPercentageStringDecimal(n, props.toFixedValue)
      : asPercentageString(n, props.value);

  const formatValueMoney = (n) => {
    const isPercent = props.currency === "%" && props.value > 0 && props.value < 1;

    const formatOptions = isPercent
      ? {
          output: "percent",
          mantissa: 1,
          trimMantissa: false
        }
      : {
          thousandSeparated: true,
          mantissa: props.decimal || 0,
          trimMantissa: false
        };

    const formatted = numbro(n).format(formatOptions);

    return formatted.replace("%", "");
  };

  const defaultValue = (n) => {
    return numbro(n).format({
      thousandSeparated: true,
      mantissa: props.decimal || 0,
      trimMantissa: false
    });
  };

  const formatValue = (type) => {
    if (type === "money") {
      return formatValueMoney;
    } else if (type === "percentage") {
      return formatValuePercent;
    } else if (type === "default") {
      return defaultValue;
    } else {
      return formatValueNumber;
    }
  };

  const animatedProps = {
    style,
    component: "h5",
    value: props.value || 0,
    initialValue: props.disableAnimation ? +props.value : 0,
    ["data-testid"]: props.testId,
    className: "data-display__number",
    duration: props.duration,
    stepPrecision: props.currency ? 1 : 0,
    formatValue: formatValue(props.type)
  };

  const renderCurrencySymbol = (position) => {
    const symbol = currencySymbols[props.currency];
    if (symbol === position || (!symbol && position === POSITION.START)) {
      return (
        <span className="data-display__currency" style={style}>
          <sup style={{ fontSize: "60%" }}>{props.currency}</sup>
        </span>
      );
    }
  };

  const Display = () => (
    <>
      <span
        style={{ display: "inline-block" }}
        data-tooltip-html={
          props.type === "number" ? handleTooltip(props.toolTipContent) : props.toolTipContent
        }
        data-tooltip-id={props.toolTipId}
        data-testid="dataDisplay"
      >
        {props.type === "money" && renderCurrencySymbol(POSITION.START)}
        {props.url !== "" ? (
          <a style={styles.cta} href={props.url} target="_raw_data_tab">
            <AnimatedNumber {...animatedProps} />
          </a>
        ) : props.customValue ? (
          <p className="data-display__value" style={style} data-testid={props.testId}>
            {props.customValue}
          </p>
        ) : (
          <AnimatedNumber {...animatedProps} />
        )}
        {props.type === "money" && renderCurrencySymbol(POSITION.END)}
        {props.toolTipId ? <Tooltip id={props.toolTipId} float /> : ""}
      </span>
      {props.changeProps && <PercentChanged {...props.changeProps} />}
      {props.averageProps && <Average {...props.averageProps} />}
    </>
  );

  return props.label ? (
    <div className={props.sectionProps?.rootClassName}>
      <span className="section-container">
        <h5
          className="data-display__label dashboardModuleSectionContainer"
          style={{ padding: 0, ...props.sectionProps?.style }}
          data-testid={props.label}
        >
          {props.label}
        </h5>
      </span>
      <div className="dashboardModuleSectionBody">
        <div id="me">
          <Display />
        </div>
      </div>
    </div>
  ) : (
    <Display />
  );
}

DataDisplay.propTypes = {
  type: PropTypes.string,
  label: PropTypes.node,
  customStyles: PropTypes.object,
  changeProps: PropTypes.object,
  sectionProps: PropTypes.object,
  averageProps: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.number,
  color: PropTypes.string,
  url: PropTypes.string,
  disableAnimation: PropTypes.bool,
  duration: PropTypes.bool,
  isPercentage: PropTypes.bool,
  toolTipContent: PropTypes.string,
  toolTipId: PropTypes.string,
  showDecimal: PropTypes.bool,
  toFixedValue: PropTypes.number,
  currency: PropTypes.string,
  testId: PropTypes.string,
  currencyLocation: PropTypes.string,
  decimals: PropTypes.number
};

let styles = {
  cta: {
    textDecoration: "none"
  }
};

// eslint-disable-next-line react/prop-types
function Average({ toolTip = "", fontSize = "16px", average, format = {}, symbol = "" }) {
  if (average == null) return null;

  let formattedVal = numbro(average).format({ thousandSeparated: true, ...format });

  return (
    <div style={{ ...averageStyles.container, fontSize }}>
      <span
        className="data-display__average"
        data-tooltip-content={toolTip}
        data-tooltip-id="average-tooltip"
        style={averageStyles.withBorder}
      >
        {formattedVal}
        {symbol}
      </span>
      {toolTip ? <Tooltip id="average-tooltip" float /> : ""}
    </div>
  );
}

const averageStyles = {
  container: {
    display: "inline-block"
  },
  withBorder: {
    padding: "2px 10px",
    fontWeight: "800",
    border: "1px solid grey",
    borderRadius: 5,
    position: "relative",
    left: "10px",
    display: "inline",
    top: "-5px"
  }
};

export default Radium(DataDisplay);
